import { useState } from 'react';
import { useSaveLeadProfile } from '../../lib/data';
import type { BoardLead } from '../../lib/types';
import { useToast } from '../../ui/kit';

const FIELDS = [
  ['wants', 'Wants'],
  ['our_plan', 'Our plan'],
  ['background', 'Story so far'],
] as const;

/** What the team knows about the lead besides contact data. Spec: docs/specs/lead-book.md. */
export function LeadAbout({ lead, canEdit }: { lead: BoardLead; canEdit: boolean }) {
  const save = useSaveLeadProfile();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState(() => ({
    website: lead.website ?? '',
    wants: lead.wants ?? '',
    our_plan: lead.our_plan ?? '',
    background: lead.background ?? '',
    brought_by: lead.brought_by ?? '',
  }));
  const answers = Object.entries(lead.form_answers ?? {});
  const empty =
    !lead.website && !lead.wants && !lead.our_plan && !lead.background && !answers.length;

  function submit() {
    save.mutate(
      { lead: lead.id, profile: f },
      {
        onSuccess: () => {
          setEditing(false);
          toast('Saved');
        },
        onError: (e) => toast(e.message),
      },
    );
  }

  return (
    <div className="blk">
      <h3 style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <span className="lbl g">About the lead</span>
        {lead.warm && <span className="pill sm warm">Warm</span>}
        {canEdit && !editing && (
          <button
            className="btn sm"
            style={{ marginLeft: 'auto' }}
            onClick={() => setEditing(true)}
          >
            Edit
          </button>
        )}
      </h3>
      {editing ? (
        <div className="card list-pad" style={{ display: 'grid', gap: 10, padding: 14 }}>
          <label className="field">
            <span>Website</span>
            <input
              aria-label="Website"
              value={f.website}
              onChange={(e) => setF({ ...f, website: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Brought by</span>
            <input
              aria-label="Brought by"
              value={f.brought_by}
              onChange={(e) => setF({ ...f, brought_by: e.target.value })}
            />
          </label>
          {FIELDS.map(([key, label]) => (
            <label className="field" key={key}>
              <span>{label}</span>
              <textarea
                aria-label={label}
                style={{ minHeight: 70 }}
                value={f[key]}
                onChange={(e) => setF({ ...f, [key]: e.target.value })}
              />
            </label>
          ))}
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={lead.warm}
              onChange={(e) => save.mutate({ lead: lead.id, profile: { warm: e.target.checked } })}
            />
            Warm lead
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn primary sm" disabled={save.isPending} onClick={submit}>
              Save
            </button>
            <button className="btn sm" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="card list-pad" style={{ display: 'grid', gap: 8, padding: 14 }}>
          {empty && <div className="empty">Nothing noted yet.</div>}
          {lead.website && (
            <div>
              <span className="muted">Website </span>
              <a href={lead.website} target="_blank" rel="noreferrer">
                {lead.website}
              </a>
            </div>
          )}
          {lead.brought_by && (
            <div>
              <span className="muted">Brought by </span>
              {lead.brought_by}
            </div>
          )}
          {FIELDS.map(([key, label]) =>
            lead[key] ? (
              <div key={key}>
                <span className="muted">{label}: </span>
                {lead[key]}
              </div>
            ) : null,
          )}
          {answers.length > 0 && (
            <div>
              <span className="muted">Form answers</span>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {answers.map(([q, a]) => (
                  <li key={q}>
                    {q}: <b>{a}</b>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
